/** @jsxImportSource @emotion/react */

import * as React from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { useFocus } from '../../hooks/focus'
import Checkmark from '../icons/CheckmarkIcon'
import { generateHash } from '../../utils/random'
import { Primary, mixins } from '@sphinx/components-styles'

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  indeterminate?: boolean
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  disabled,
  children,
  indeterminate,
  ...props
}) => {
  const id = props.id || generateHash()
  const checkboxRef = React.useRef<HTMLInputElement>()
  const { focused, handleClick, handleMouseUp } = useFocus(props)

  React.useEffect(() => {
    if (checkboxRef && checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  return (
    <CheckboxWithLabel htmlFor={id} onMouseUp={handleMouseUp}>
      <CheckboxControl
        id={id}
        {...props}
        type="checkbox"
        focused={focused}
        ref={checkboxRef}
        checked={checked}
        disabled={disabled}
        onClick={handleClick}
      />
      <StyledCheckbox indeterminate={indeterminate}>
        {checked && (
          <Checkmark
            css={css`
              position: absolute;
              top: calc(50% - 10px);
              left: calc(50% - 10px);

              & path {
                fill: white;
              }
            `}
          />
        )}
      </StyledCheckbox>
      {children && (
        <Label
          htmlFor={id}
          css={css`
            cursor: ${disabled ? 'initial' : 'pointer'};
            color: ${disabled ? Primary.navy[20] : Primary.navy[100]};
          `}
        >
          {children}
        </Label>
      )}
    </CheckboxWithLabel>
  )
}

const Label = styled.label`
  ${mixins.body}
  margin-left: 8px;
`

const CheckboxControl = styled.input<any>`
  ${({ disabled, focused }) =>
    focused &&
    !disabled &&
    css`
      &:focus + div {
        outline-color: ${Primary.orange[100]};
        outline-offset: 2px;
        outline-style: solid;
      }
    `}

  &:checked {
    & + div {
      border: 1px solid ${Primary.orange[100]};
      border-radius: 2px;
      background: ${Primary.orange[100]};

      &:hover {
        background: ${Primary.orange[80]};
        border: 1px solid ${Primary.orange[80]};
      }
    }

    &:disabled {
      & + div {
        &,
        &:hover {
          background: ${Primary.navy[20]};
          border: 1px solid ${Primary.navy[20]};
        }
      }
    }
  }

  &:disabled + div {
    &,
    &:hover {
      cursor: initial;
      background: ${Primary.neutral.white};
      border: 1px solid ${Primary.navy[20]};
    }
  }

  &:indeterminate {
    & + div {
      border: 1px solid ${Primary.orange[100]};
      border-radius: 2px;
      background: ${Primary.neutral.white};

      &:hover {
        background: ${Primary.neutral.white};
        border: 1px solid ${Primary.orange[80]};
      }
    }

    &:disabled {
      & + div {
        border: 1px solid ${Primary.navy[20]};

        &::after {
          background: ${Primary.navy[20]};
        }
      }
    }
  }
`

const CheckboxWithLabel = styled.label`
  display: flex;
  padding: 6px 8px;
  width: max-content;
  align-items: center;
  flex-flow: row nowrap;
`

const StyledCheckbox = styled.div<CheckboxProps>`
  width: 20px;
  height: 20px;
  outline: none;
  margin-left: -19px;
  position: relative;
  box-sizing: border-box;
  border-radius: 2px;
  border: 1px solid ${Primary.navy[40]};
  background: ${Primary.neutral.white};

  &:hover {
    cursor: pointer;
    border: 1px solid ${Primary.navy[100]};

    &::after {
      background: ${Primary.orange[80]};
    }
  }

  ${({ indeterminate }) =>
    indeterminate &&
    css`
      &::after {
        content: '';
        width: 12.5px;
        height: 12.5px;
        border-radius: 2px;
        background: ${Primary.orange[100]};
        position: absolute;
        top: calc(50% - 6.25px);
        left: calc(50% - 6.25px);
      }
    `}
`
